import React, { ReactElement } from 'react';
import { makeStyles } from '@mui/styles';
import { IconButton, TextField } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { colors, theme } from '../../theme';
import { Header } from '../shared/header/header';
import { SingleSelectDropDown } from '../shared/dropdown/singleSelect';
import { CustomButton } from '../shared/button/button';
import { ItemType } from '../../types/common.types';
import { CustomAlert } from '../shared/alert/alert';

type AddCertificateDocumentType = {
	documentTypeOptions: ItemType[];
	documentType: string;
	otherDocumentType: string;
	showOtherDocumentType: boolean;
	selectedFile?: File;
	error: string;
	inputRef: React.RefObject<HTMLInputElement>;
	onSelectDocumentType: Function;
	onOtherDocumentTypeChange: Function;
	onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onDiscard: Function;
	onSave: Function;
	onCloseError: Function;
};

const color = colors();
const themes = theme().typography;

export const AddCertificateDocument = ({
	documentTypeOptions,
	documentType,
	otherDocumentType,
	showOtherDocumentType,
	selectedFile,
	error,
	inputRef,
	onSelectDocumentType,
	onOtherDocumentTypeChange,
	onFileChange,
	onDiscard,
	onSave,
	onCloseError,
}: AddCertificateDocumentType): ReactElement => {
	const classes = useStyles();

	return (
		<div className={classes.container}>
			<div className={classes.flexDisplay}>
				<IconButton id="back-button" onClick={() => onDiscard()}>
					<ArrowBackIcon className={classes.backButton} />
				</IconButton>
				<Header title="Add new document" />
			</div>
			<div className={classes.formContent}>
				{error.length > 0 && (
					<div className={classes.alert}>
						<CustomAlert
							open={error.length > 0}
							message={error.split('|').map((e) => (
								<>
									{e.length > 0 && (
										<>
											{e}
											<br />
										</>
									)}
								</>
							))}
							severity="error"
							onCloseCallback={() => onCloseError()}
						/>
					</div>
				)}
				<div className={classes.formRow}>
					<div className={classes.label}>
						Document Type<span className={classes.required}>*</span>
					</div>
					<SingleSelectDropDown
						label=""
						width={539}
						data={documentTypeOptions}
						value={documentType}
						onSelectCallback={onSelectDocumentType}
					/>
				</div>
				{showOtherDocumentType && (
					<div className={classes.formRow}>
						<div className={classes.label}>
							Other Document Type<span className={classes.required}>*</span>
						</div>
						<TextField
							id="other-document-type"
							size="small"
							value={otherDocumentType}
							onChange={(e) => onOtherDocumentTypeChange(e.target.value)}
							inputProps={{ maxLength: 161 }}
							sx={{ m: 1, width: 539 }}
						/>
					</div>
				)}
				<div className={classes.formRow}>
					<div className={classes.label}>
						Upload file<span className={classes.required}>*</span>
					</div>
					<div className={classes.uploadContainer}>
						<CustomButton
							id="choose-file"
							text="Choose file"
							width={120}
							onClickCallback={() => inputRef.current?.click()}
						/>
						<div className={classes.fileName}>{selectedFile?.name || 'No file selected'}</div>
						<input
							ref={inputRef}
							type="file"
							accept=".pdf,.doc,.docx,.xlsx,.xls"
							style={{ display: 'none' }}
							onChange={onFileChange}
						/>
					</div>
				</div>
				<div className={classes.footer}>
					<CustomButton id="discard" text="Discard" variant="contained" onClickCallback={() => onDiscard()} />
					<CustomButton id="save" text="Save" variant="contained" onClickCallback={() => onSave()} />
				</div>
			</div>
		</div>
	);
};

const useStyles = makeStyles(() => ({
	container: {
		paddingTop: '10px',
	},
	flexDisplay: {
		display: 'flex',
		alignItems: 'center',
		gap: '10px',
	},
	backButton: {
		width: '40px !important',
		height: '40px !important',
		color: `${color.shades.black} !important`,
	},
	formContent: {
		maxWidth: '760px',
		margin: '20px auto 0',
	},
	alert: {
		marginBottom: '15px',
	},
	formRow: {
		display: 'grid',
		gridTemplateColumns: '190px 1fr',
		alignItems: 'center',
		marginBottom: '10px',
		'& .MuiFormControl-root, & .MuiTextField-root': {
			margin: '0 !important',
		},
	},
	label: {
		...themes.bodyMedium,
		display: 'flex',
		justifyContent: 'flex-end',
		paddingRight: '12px',
	},
	required: {
		color: color.signal.error,
	},
	uploadContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: '12px',
	},
	fileName: {
		...themes.bodyRegular,
		color: color.neutral[8],
	},
	footer: {
		display: 'flex',
		justifyContent: 'end',
		paddingTop: '20px',
		gap: '8px',
		borderTop: `1px solid ${color.neutral[3]}`,
		marginTop: '24px',
		'& #save': {
			backgroundColor: `${color.primary[100]} !important`,
			color: `${color.text.light} !important`,
		},
		'& #save:hover': {
			backgroundColor: `${color.primary[90]} !important`,
		},
		'& #discard': {
			backgroundColor: `${color.neutral[2]} !important`,
			color: `${color.text.dark} !important`,
		},
	},
}));
